import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import styled from 'styled-components/macro'
import { themeGet } from '@styled-system/theme-get'
import { v4 as uuidv4 } from 'uuid'
import { parseCSV } from 'src/Utils/csv/parseCSV'
import { MIME_TYPE } from 'src/Utils/globalEnums'
import {
  CsvV2TemplatePanel,
  CsvV2UploadPanelBody,
} from 'src/components/ModalForms/Reusable/UploadCSVModalV2.components'
import { getDownloadCSVFunc } from 'src/components/ModalForms/Reusable/UploadCSVModal.helpers'
import {
  CsvV2ModalSubtitle as ModalSubtitle,
  CsvV2PanelsRow as PanelsRow,
  CsvV2UploadPanel as UploadPanel,
} from '../Reusable/UploadCSVModalV2.styles'
import { SectionDraft } from './FrameworkSectionsEditor.types'
import {
  FRAMEWORK_SECTIONS_CSV_HEADERS,
  FRAMEWORK_SECTIONS_CSV_TEMPLATE,
  ImportFrameworkSectionsCSVRow,
} from './AddFramework/FrameworkSectionsCSV.constants'
import {
  getFrameworkSectionsCsvParsers,
  getFrameworkSectionsRowValidators,
} from './AddFramework/FrameworkSectionsCSV.helpers'

const CsvOuterCard = styled.div`
  width: 100%;
  border: 1px solid ${themeGet('colors.border.neutral.light')};
  border-radius: 12px;
  background-color: ${themeGet('colors.bg.neutral')};
  padding: 16px;
`

export const csvRowsToSectionDrafts = (
  rows: ImportFrameworkSectionsCSVRow[],
): SectionDraft[] => {
  const sectionsByKey = new Map<string, SectionDraft>()
  for (const row of rows) {
    const sectionName = row.sectionName.trim()
    if (!sectionName) continue
    const key = sectionName.toLowerCase()
    let section = sectionsByKey.get(key)
    if (!section) {
      section = {
        tempId: uuidv4(),
        name: sectionName,
        description: row.sectionDetails.trim(),
        requirements: [],
      }
      sectionsByKey.set(key, section)
    }
    section.requirements.push({
      tempId: uuidv4(),
      name: row.requirementName.trim(),
      description: row.requirementDetails.trim(),
      controlIds: row.controlMappings,
    })
  }
  return Array.from(sectionsByKey.values())
}

export type FrameworkSectionsCSVFormProps = {
  existingRequirementNames: string[]
  validControlIdSet: Set<string>
  isSubmitting: boolean
  isLoadingControls: boolean
  showAttachmentError: boolean
  onParseChange: (parsed: {
    items: ImportFrameworkSectionsCSVRow[]
    errors: string[]
    file: File | null
  }) => void
  externalErrors?: string[]
}

export const FrameworkSectionsCSVForm = ({
  existingRequirementNames,
  validControlIdSet,
  isSubmitting,
  isLoadingControls,
  showAttachmentError,
  onParseChange,
  externalErrors = [],
}: FrameworkSectionsCSVFormProps): JSX.Element => {
  const [file, setFile] = useState<File | null>(null)
  const [uploadItems, setUploadItems] = useState<
    ImportFrameworkSectionsCSVRow[]
  >([])
  const [parseErrors, setParseErrors] = useState<string[]>([])

  useEffect(() => {
    onParseChange({ items: uploadItems, errors: parseErrors, file })
  }, [uploadItems, parseErrors, file, onParseChange])

  const parsers = useMemo(
    () =>
      getFrameworkSectionsCsvParsers(
        validControlIdSet,
        existingRequirementNames,
      ),
    [validControlIdSet, existingRequirementNames],
  )

  const parseCSVCallback = useMemo(
    () =>
      parseCSV<ImportFrameworkSectionsCSVRow>({
        headers: FRAMEWORK_SECTIONS_CSV_HEADERS,
        columnParsers: parsers,
        rowValidators: getFrameworkSectionsRowValidators(),
        setErrors: setParseErrors,
        setData: setUploadItems,
      }),
    [parsers],
  )

  const handleFileUpload = useCallback(
    async (uploadedFile: File) => {
      setParseErrors([])
      setUploadItems([])
      setFile(uploadedFile)
      await parseCSVCallback(uploadedFile)
    },
    [parseCSVCallback],
  )

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const uploadedFile = acceptedFiles[0]
      if (!uploadedFile) return
      await handleFileUpload(uploadedFile)
    },
    [handleFileUpload],
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { [MIME_TYPE.TEXT_CSV]: ['.csv'] },
    multiple: false,
    noClick: true,
    noKeyboard: true,
    disabled: isSubmitting || isLoadingControls,
  })

  const clearFile = useCallback(() => {
    setFile(null)
    setParseErrors([])
    setUploadItems([])
  }, [])

  const downloadTemplate = useMemo(
    () =>
      getDownloadCSVFunc(
        FRAMEWORK_SECTIONS_CSV_TEMPLATE,
        'framework_sections_example',
      ),
    [],
  )

  const requirementCount = uploadItems.length
  const sectionCount = useMemo(
    () =>
      new Set(
        uploadItems
          .map((item) => item.sectionName.trim().toLowerCase())
          .filter(Boolean),
      ).size,
    [uploadItems],
  )
  const mappedControlCount = useMemo(
    () =>
      new Set(
        uploadItems.flatMap((item) => item.controlMappings).filter(Boolean),
      ).size,
    [uploadItems],
  )

  const combinedErrors = [...externalErrors, ...parseErrors]

  return (
    <>
      <ModalSubtitle>
        Upload a CSV to add sections and requirements to this framework. Each
        row is a requirement; rows sharing a section name are grouped into that
        section. Control mappings are optional.
      </ModalSubtitle>
      <CsvOuterCard>
        <PanelsRow>
          <CsvV2TemplatePanel
            instruction="Your CSV should have one row per requirement with the requirement name(required), requirement details(optional), section(required), section details(optional), and comma-separated control model IDs."
            sampleText={
              <>
                {FRAMEWORK_SECTIONS_CSV_HEADERS.map((header, index) => (
                  <React.Fragment key={header.output}>
                    {header.input}
                    {index < FRAMEWORK_SECTIONS_CSV_HEADERS.length - 1
                      ? ', '
                      : ''}
                  </React.Fragment>
                ))}
              </>
            }
            onDownloadTemplate={downloadTemplate}
          />
          <UploadPanel {...getRootProps()} $isDragActive={isDragActive}>
            <input {...getInputProps()} />
            <CsvV2UploadPanelBody
              isLoading={isSubmitting || isLoadingControls}
              file={file}
              onClearFile={clearFile}
              summaries={[
                { label: 'Section(s)', value: sectionCount },
                { label: 'Requirement(s)', value: requirementCount },
                { label: 'Control(s)', value: mappedControlCount || 0 },
              ]}
              errors={combinedErrors}
              showAttachmentError={showAttachmentError}
              onBrowse={open}
            />
          </UploadPanel>
        </PanelsRow>
      </CsvOuterCard>
    </>
  )
}
